import { useState, useRef, useEffect } from 'react'
import { useKeyboardSafeBottom } from '@/hooks/useKeyboardSafeBottom'
import { Send, Image, ListTodo, X, Mic, Paperclip, Camera, Plus, FileText } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { MentionAutocomplete } from './MentionAutocomplete'
import type { ChatMessage, ChatMessagePayload, SharedGroupMember, SharedTask } from '@/lib/sharedTypes'
import { formatDate } from '@/utils/format'

const MAX_IMAGE_SIZE = 200 * 1024 * 1024 // 200MB
const MAX_FILE_SIZE = 1000 * 1024 * 1024 // 1GB

interface ChatComposerProps {
  messages: ChatMessage[]
  members: SharedGroupMember[]
  tasks: SharedTask[]
  lastReadChatAt?: string
  replyingTo: ChatMessage | null
  onSend: (payload: ChatMessagePayload) => Promise<void>
  onUploadImage: (file: File) => Promise<{ url: string; width: number; height: number; name: string }>
  onUploadVoice?: (blob: Blob) => Promise<{ url: string; duration: number }>
  onUploadFile?: (file: File) => Promise<{ url: string; name: string; size: number; type: string }>
  onTyping?: (isTyping: boolean) => void
  onAddTask?: (task: { title: string; priority?: string; dueDate?: string }) => Promise<void>
  onClearReply: () => void
  onOpenTaskPicker: () => void
  onOpenMemoPicker: () => void
}

/**
 * 채팅 입력 영역 — 텍스트/멘션/타이핑/첨부/녹음 상태를 자체적으로 관리한다.
 * 메시지 목록(ChatPanel)과 분리되어, 키 입력마다 목록이 리렌더되지 않도록 한다.
 */
export function ChatComposer({
  messages, members, tasks, lastReadChatAt,
  replyingTo,
  onSend, onUploadImage, onUploadVoice, onUploadFile, onTyping, onAddTask,
  onClearReply, onOpenTaskPicker, onOpenMemoPicker,
}: ChatComposerProps) {
  const [text, setText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const isSendingRef = useRef(false) // 동기적 이중 전송 방지 (useState는 비동기라 더블탭 통과)
  const [showPlusMenu, setShowPlusMenu] = useState(false)
  const [imagePreview, setImagePreview] = useState<{ file: File; url: string } | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [recordingDuration, setRecordingDuration] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const fileInputRefGeneral = useRef<HTMLInputElement>(null)

  // iOS 키보드 안전 영역
  const kbOffset = useKeyboardSafeBottom()

  // 멘션 자동완성
  const [mentionQuery, setMentionQuery] = useState('')
  const [showMentionPopup, setShowMentionPopup] = useState(false)
  const [mentionStart, setMentionStart] = useState(-1)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 부모에서 인라인 arrow로 전달되는 콜백 — ref로 최신값 유지 (effect deps 재실행 방지)
  const onTypingRef = useRef(onTyping)
  onTypingRef.current = onTyping

  // B2: imagePreview objectURL 언마운트 시 정리
  useEffect(() => {
    const url = imagePreview?.url
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [imagePreview?.url])

  // B3: 녹음 중 언마운트 시 cleanup
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current) {
        try {
          mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop())
          mediaRecorderRef.current.stop()
        } catch { /* already stopped */ }
      }
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current)
    }
  }, [])

  // 언마운트 또는 페이지 이탈 시 타이핑 상태 false로 리셋
  useEffect(() => {
    const handleBeforeUnload = () => { onTypingRef.current?.(false) }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      onTypingRef.current?.(false)
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
    }
  }, [])

  // 멘션 감지 + 타이핑 인디케이터
  const handleTextChange = (value: string) => {
    setText(value)
    if (showPlusMenu) setShowPlusMenu(false)

    // 타이핑 인디케이터 디바운스
    if (onTyping) {
      onTyping(true)
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
      typingTimerRef.current = setTimeout(() => onTyping(false), 3000)
    }

    const cursorPos = textareaRef.current?.selectionStart || value.length
    const textBefore = value.substring(0, cursorPos)
    const atIndex = textBefore.lastIndexOf('@')

    if (atIndex >= 0 && (atIndex === 0 || textBefore[atIndex - 1] === ' ' || textBefore[atIndex - 1] === '\n')) {
      const query = textBefore.substring(atIndex + 1)
      if (!query.includes(' ') && !query.includes('\n')) {
        setMentionQuery(query)
        setMentionStart(atIndex)
        setShowMentionPopup(true)
        return
      }
    }
    setShowMentionPopup(false)
  }

  const handleMentionSelect = (member: SharedGroupMember) => {
    const before = text.substring(0, mentionStart)
    const after = text.substring(mentionStart + mentionQuery.length + 1)
    setText(`${before}@${member.displayName} ${after}`)
    setShowMentionPopup(false)
    textareaRef.current?.focus()
  }

  const parseMentionUids = (messageText: string): string[] => {
    const uids: string[] = []
    for (const member of members) {
      if (messageText.includes(`@${member.displayName}`)) {
        uids.push(member.uid)
      }
    }
    return uids
  }

  // 메시지 전송
  const handleSend = async () => {
    if (isSendingRef.current) return
    isSendingRef.current = true

    // 이미지 전송
    if (imagePreview) {
      setIsSending(true)
      try {
        const result = await onUploadImage(imagePreview.file)
        await onSend({
          text: text.trim(),
          type: 'image',
          imageUrl: result.url,
          imageWidth: result.width,
          imageHeight: result.height,
          imageName: result.name,
          replyTo: replyingTo ? {
            messageId: replyingTo.id,
            userId: replyingTo.userId,
            userName: replyingTo.userName,
            text: replyingTo.text,
            imageUrl: replyingTo.imageUrl,
          } : undefined,
          mentions: parseMentionUids(text),
        })
        setImagePreview(null)
        setText('')
        onClearReply()
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        console.error('[ChatComposer] 이미지 전송 실패:', msg, err)
        const { useToastStore } = await import('@/stores/toastStore')
        useToastStore.getState().showToast(`이미지 전송 실패: ${msg}`, 'error')
      } finally {
        isSendingRef.current = false
        setIsSending(false)
      }
      return
    }

    if (!text.trim()) { isSendingRef.current = false; return }

    const trimmed = text.trim()

    // 슬래시 커맨드 처리
    if (trimmed.startsWith('/')) {
      const handled = await processSlashCommand(trimmed, tasks, onSend, onAddTask, lastReadChatAt, messages)
      if (handled) {
        setText('')
        onClearReply()
        isSendingRef.current = false
        return
      }
    }

    // @AI 처리
    if (trimmed.includes('@AI') || trimmed.includes('@ai')) {
      setIsSending(true)
      try {
        await onSend({ text: trimmed, type: 'text', mentions: parseMentionUids(trimmed) })
        setText('')
        const { getAIConfig, callAI } = await import('@/services/aiService')
        const { provider, apiKey } = getAIConfig()
        const query = trimmed.replace(/@AI/gi, '').trim()
        const taskContext = tasks.slice(0, 30).map((t) =>
          `[${t.status}] ${t.title}${t.dueDate ? ` (마감: ${t.dueDate})` : ''}${(t.assigneeNames?.join(', ') || t.assigneeName) ? ` (담당: ${t.assigneeNames?.join(', ') || t.assigneeName})` : ''}`
        ).join('\n')
        const memberNames = members.map((m) => m.displayName).join(', ')
        const systemPrompt = `당신은 TDL 프로젝트 관리 AI 어시스턴트입니다. 그룹 멤버: ${memberNames}\n\n현재 할일 목록:\n${taskContext}\n\n사용자 질문에 한국어로 간결하게 답변하세요. 마감 임박, 과부하, 미완료 분석 등을 제공합니다.`
        const aiResponse = await callAI(provider, apiKey, systemPrompt, [{ role: 'user', content: query }], 'chat', 1024)
        await onSend({ text: aiResponse, type: 'system', systemEvent: { action: 'ai_response', actorName: 'AI 어시스턴트' } })
      } catch (err) {
        await onSend({ text: `AI 응답 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`, type: 'system', systemEvent: { action: 'ai_response', actorName: 'AI 어시스턴트' } })
      } finally {
        isSendingRef.current = false
        setIsSending(false)
        onClearReply()
      }
      return
    }

    // 일반 텍스트 전송 — 텍스트 먼저 클리어 후 서버 전송
    const replySnapshot = replyingTo ? {
      messageId: replyingTo.id,
      userId: replyingTo.userId,
      userName: replyingTo.userName,
      text: replyingTo.text,
      imageUrl: replyingTo.imageUrl,
    } : undefined
    const mentionUids = parseMentionUids(trimmed)
    setText('')
    onClearReply()
    setIsSending(true)
    try {
      await onSend({
        text: trimmed,
        type: 'text',
        mentions: mentionUids,
        replyTo: replySnapshot,
      })
    } catch (err) {
      setText(trimmed) // 실패 시 텍스트 복원
      console.error('[ChatComposer] 메시지 전송 실패:', err)
      const msg = err instanceof Error ? err.message : String(err)
      import('@/stores/toastStore').then(({ useToastStore }) => {
        useToastStore.getState().showToast(`전송 실패: ${msg}`, 'error')
      })
    } finally {
      isSendingRef.current = false
      setIsSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.repeat) return // Enter 키 반복 입력(auto-repeat) 차단
    if (showMentionPopup) return // MentionAutocomplete handles keyboard
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    // files를 복사한 후 input 리셋 (value=''하면 FileList 참조가 무효화됨)
    const fileArray = Array.from(files)
    e.target.value = ''

    // 파일 크기 검증
    for (const file of fileArray) {
      if (file.size > MAX_IMAGE_SIZE) {
        import('@/stores/toastStore').then(({ useToastStore }) => {
          useToastStore.getState().showToast(`이미지 크기가 200MB를 초과합니다: ${file.name}`, 'error')
        })
        return
      }
    }

    // 파일 판별
    const isImageFile = (f: File) =>
      f.type.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|heif|bmp|svg)$/i.test(f.name)
    const isVideoFile = (f: File) =>
      f.type.startsWith('video/') || /\.(mp4|mov|avi|webm|mkv)$/i.test(f.name)

    // 동영상 파일은 file 업로드로 전환 (type='video')
    const videoFiles = fileArray.filter(isVideoFile)
    if (videoFiles.length > 0 && onUploadFile) {
      if (isSendingRef.current) return
      isSendingRef.current = true
      setIsSending(true)
      try {
        for (const vf of videoFiles) {
          const result = await onUploadFile(vf)
          await onSend({ text: '', fileUrl: result.url, fileName: result.name, fileSize: result.size, fileType: result.type, type: 'video' })
        }
      } finally {
        isSendingRef.current = false
        setIsSending(false)
      }
      if (videoFiles.length === fileArray.length) return // 모든 파일이 동영상이면 종료
    }

    // 다중 사진: 1장이면 프리뷰, 여러 장이면 순차 전송
    const imageFiles = fileArray.filter(isImageFile)
    if (imageFiles.length === 0) return
    if (imageFiles.length === 1) {
      const file = imageFiles[0]
      setImagePreview({ file, url: URL.createObjectURL(file) })
    } else {
      if (isSendingRef.current) return
      isSendingRef.current = true
      setIsSending(true)
      try {
        for (const file of fileArray) {
          if (!isImageFile(file)) continue
          const result = await onUploadImage(file)
          await onSend({
            text: '',
            type: 'image',
            imageUrl: result.url,
            imageWidth: result.width,
            imageHeight: result.height,
            imageName: result.name,
          })
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        alert(`이미지 전송 실패: ${msg}`)
      } finally {
        isSendingRef.current = false
        setIsSending(false)
      }
    }
  }

  // 음성 녹음
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : ''
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {})
      audioChunksRef.current = []

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data)
      }

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop())
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current)
        setRecordingDuration(0)

        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
        if (blob.size < 1000) return // 너무 짧은 녹음 무시

        if (!onUploadVoice) return
        if (isSendingRef.current) return
        isSendingRef.current = true
        setIsSending(true)
        try {
          const result = await onUploadVoice(blob)
          await onSend({
            text: '',
            type: 'voice',
            voiceUrl: result.url,
            voiceDuration: result.duration,
            replyTo: replyingTo ? {
              messageId: replyingTo.id,
              userId: replyingTo.userId,
              userName: replyingTo.userName,
              text: replyingTo.text,
            } : undefined,
          })
          onClearReply()
        } finally {
          isSendingRef.current = false
          setIsSending(false)
        }
      }

      mediaRecorder.start()
      mediaRecorderRef.current = mediaRecorder
      setIsRecording(true)
      setRecordingDuration(0)
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((d) => d + 1)
      }, 1000)
    } catch {
      // 마이크 권한 거부
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
    }
  }

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.ondataavailable = null
      mediaRecorderRef.current.onstop = null
      mediaRecorderRef.current.stop()
      mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop())
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current)
      setIsRecording(false)
      setRecordingDuration(0)
    }
  }

  // 파일 첨부
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    if (file.size > MAX_FILE_SIZE) {
      import('@/stores/toastStore').then(({ useToastStore }) => {
        useToastStore.getState().showToast('파일 크기가 1GB를 초과합니다.', 'error')
      })
      return
    }

    if (!onUploadFile) return
    if (isSendingRef.current) return
    isSendingRef.current = true
    setIsSending(true)
    try {
      const result = await onUploadFile(file)
      await onSend({
        text: result.name,
        type: 'file',
        fileUrl: result.url,
        fileName: result.name,
        fileSize: result.size,
        fileType: result.type,
        replyTo: replyingTo ? {
          messageId: replyingTo.id,
          userId: replyingTo.userId,
          userName: replyingTo.userName,
          text: replyingTo.text,
        } : undefined,
      })
      onClearReply()
    } finally {
      isSendingRef.current = false
      setIsSending(false)
    }
  }

  // 카메라 촬영
  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setImagePreview({ file, url: URL.createObjectURL(file) })
  }

  const formatRecordingTime = (sec: number) => {
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  return (
    <>
      {/* 이미지 프리뷰 + 전송 버튼 */}
      {imagePreview && (
        <div className="px-3 pt-2 flex items-center gap-2">
          <img src={imagePreview.url} className="w-16 h-16 rounded-lg object-cover" />
          <span className="text-xs text-caption flex-1 truncate">{imagePreview.file.name}</span>
          <button
            onClick={() => setImagePreview(null)}
            className="p-1 text-disabled hover:text-red-500"
          >
            <X className="w-4 h-4" />
          </button>
          <button
            onClick={handleSend}
            disabled={isSending}
            className="px-3 py-1.5 bg-primary-500 text-white text-xs font-medium rounded-lg hover:bg-primary-600 disabled:opacity-50"
          >
            {isSending ? '전송중...' : '전송'}
          </button>
        </div>
      )}

      {/* 입력 영역 */}
      <div className="border-t border-base pb-safe" style={kbOffset > 0 ? { paddingBottom: kbOffset } : undefined}>
        {/* 멘션 자동완성 */}
        <div className="relative px-3 pt-1">
          <MentionAutocomplete
            query={mentionQuery}
            members={members}
            onSelect={handleMentionSelect}
            visible={showMentionPopup}
          />
        </div>

        {/* Hidden file inputs */}
        <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleImageSelect} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleCameraCapture} />
        <input ref={fileInputRefGeneral} type="file" className="hidden" onChange={handleFileSelect} />

        {/* 녹음 중 UI */}
        {isRecording ? (
          <div className="flex items-center gap-3 px-3 py-2">
            <button
              onClick={cancelRecording}
              className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-sm text-heading font-medium">녹음 중</span>
              <span className="text-sm text-caption font-mono">{formatRecordingTime(recordingDuration)}</span>
            </div>
            <button
              onClick={stopRecording}
              className="p-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="flex items-end gap-1.5 px-2 py-2">
            {/* + 버튼 (기능 메뉴 토글) */}
            <button
              onClick={() => setShowPlusMenu((v) => !v)}
              className="p-2 text-caption hover:text-heading hover-surface rounded-full transition-colors flex-shrink-0"
              aria-label={showPlusMenu ? '메뉴 닫기' : '기능 메뉴'}
            >
              {showPlusMenu ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            </button>

            {/* 텍스트 입력 */}
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => handleTextChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setShowPlusMenu(false)}
              placeholder="메시지 입력"
              rows={1}
              className="flex-1 px-3 py-2 rounded-2xl border border-base bg-surface-secondary text-sm text-heading resize-none focus:outline-none focus:ring-1 focus:ring-primary-500 max-h-[120px] [@media(orientation:landscape)and(max-height:500px)]:max-h-[72px]"
              style={{ minHeight: '36px' }}
              onInput={(e) => {
                const t = e.target as HTMLTextAreaElement
                // 가로 모드에서는 키보드가 화면을 크게 가리므로 입력창 최대 높이를 낮춘다.
                const maxH = window.matchMedia('(orientation: landscape) and (max-height: 500px)').matches ? 72 : 120
                t.style.height = '36px'
                t.style.height = `${Math.min(t.scrollHeight, maxH)}px`
              }}
            />

            {/* 전송 / 음성 녹음 */}
            {text.trim() || imagePreview ? (
              <button
                onClick={handleSend}
                disabled={isSending}
                className="p-2 bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors flex-shrink-0"
              >
                <Send className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={startRecording}
                disabled={isSending}
                className="p-2 text-caption hover:text-primary-500 hover-surface rounded-full transition-colors flex-shrink-0"
                title="음성 메시지"
              >
                <Mic className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* + 메뉴 그리드 (카카오톡 스타일) — 가로 모드에서 세로 여백 축소 */}
        {showPlusMenu && !isRecording && (
          <div className="grid grid-cols-4 gap-y-5 gap-x-2 px-4 py-5 [@media(orientation:landscape)and(max-height:500px)]:gap-y-2 [@media(orientation:landscape)and(max-height:500px)]:py-2 border-t border-base bg-surface-secondary">
            <PlusMenuItem icon={Image} label="사진" onClick={() => { fileInputRef.current?.click(); setShowPlusMenu(false) }} />
            <PlusMenuItem icon={Camera} label="카메라" onClick={() => { cameraInputRef.current?.click(); setShowPlusMenu(false) }} />
            <PlusMenuItem icon={Paperclip} label="파일" onClick={() => { fileInputRefGeneral.current?.click(); setShowPlusMenu(false) }} />
            <PlusMenuItem icon={ListTodo} label="할일 공유" onClick={() => { onOpenTaskPicker(); setShowPlusMenu(false) }} />
            <PlusMenuItem icon={Mic} label="음성 메시지" onClick={() => { startRecording(); setShowPlusMenu(false) }} />
            <PlusMenuItem icon={FileText} label="메모 공유" onClick={() => { onOpenMemoPicker(); setShowPlusMenu(false) }} />
          </div>
        )}
      </div>
    </>
  )
}

// 카카오톡 스타일 + 메뉴 아이템
function PlusMenuItem({ icon: Icon, label, onClick }: {
  icon: LucideIcon; label: string; onClick: () => void
}) {
  return (
    <button onClick={onClick} className="flex flex-col items-center gap-1.5">
      <div className="w-12 h-12 rounded-2xl bg-surface-tertiary flex items-center justify-center text-caption hover:text-heading hover:bg-[var(--active-bg)] transition-colors">
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-meta text-caption leading-tight">{label}</span>
    </button>
  )
}

// ─── 슬래시 커맨드 처리 ───
async function processSlashCommand(
  command: string,
  tasks: SharedTask[],
  onSend: (payload: ChatMessagePayload) => Promise<void>,
  onAddTask?: (task: { title: string; priority?: string; dueDate?: string }) => Promise<void>,
  lastReadChatAt?: string,
  messages?: ChatMessage[]
): Promise<boolean> {
  const cmd = command.toLowerCase().trim()
  const args = command.slice(command.indexOf(' ') + 1).trim()

  // /할일 또는 /task — 자연어 할일 생성
  if (cmd.startsWith('/할일') || cmd.startsWith('/task')) {
    if (!args || args === cmd) {
      await onSend({ text: '사용법: /할일 [내용]\n예: /할일 내일까지 디자인 시안 전달 (높음)', type: 'system', systemEvent: { action: 'slash_command' } })
      return true
    }
    const { parseNaturalLanguage } = await import('@/lib/nlParser')
    const parsed = parseNaturalLanguage(args)
    if (onAddTask) {
      try {
        await onAddTask({ title: parsed.cleanTitle, priority: parsed.priority, dueDate: parsed.dueDate })
        await onSend({
          text: `할일이 생성되었습니다: "${parsed.cleanTitle}"${parsed.dueDate ? ` (마감: ${parsed.dueDate})` : ''}`,
          type: 'system',
          systemEvent: { action: 'task_created', targetTitle: parsed.cleanTitle },
        })
      } catch {
        await onSend({ text: '할일 생성에 실패했습니다.', type: 'system', systemEvent: { action: 'slash_command' } })
      }
    }
    return true
  }

  // /마감오늘 또는 /due — 오늘 마감 할일 목록
  if (cmd.startsWith('/마감오늘') || cmd.startsWith('/due')) {
    const today = formatDate(new Date())
    const dueToday = tasks.filter((t) => t.dueDate === today && t.status === 'pending')
    const text = dueToday.length > 0
      ? `📋 오늘 마감 할일 (${dueToday.length}건):\n${dueToday.map((t) => `• ${t.title}${(t.assigneeNames?.join(', ') || t.assigneeName) ? ` (@${t.assigneeNames?.join(', ') || t.assigneeName})` : ''}`).join('\n')}`
      : '✅ 오늘 마감인 할일이 없습니다!'
    await onSend({ text, type: 'system', systemEvent: { action: 'slash_command' } })
    return true
  }

  // /미완료 또는 /pending — 미완료 할일 목록
  if (cmd.startsWith('/미완료') || cmd.startsWith('/pending')) {
    const pending = tasks.filter((t) => t.status === 'pending')
    const text = pending.length > 0
      ? `📝 미완료 할일 (${pending.length}건):\n${pending.slice(0, 15).map((t) => `• ${t.title}${t.dueDate ? ` (마감: ${t.dueDate})` : ''}${(t.assigneeNames?.join(', ') || t.assigneeName) ? ` (@${t.assigneeNames?.join(', ') || t.assigneeName})` : ''}`).join('\n')}${pending.length > 15 ? `\n... 외 ${pending.length - 15}건` : ''}`
      : '🎉 모든 할일이 완료되었습니다!'
    await onSend({ text, type: 'system', systemEvent: { action: 'slash_command' } })
    return true
  }

  // /recap — 부재 중 요약 (@AI)
  if (cmd.startsWith('/recap') || cmd.startsWith('/요약')) {
    if (!lastReadChatAt || !messages?.length) {
      await onSend({ text: '요약할 새 메시지가 없습니다.', type: 'system', systemEvent: { action: 'slash_command' } })
      return true
    }
    const newMsgs = messages.filter((m) => m.createdAt > lastReadChatAt && m.type !== 'system' && !m.isDeleted)
    if (newMsgs.length === 0) {
      await onSend({ text: '읽지 않은 새 메시지가 없습니다.', type: 'system', systemEvent: { action: 'slash_command' } })
      return true
    }
    try {
      const { getAIConfig, callAI } = await import('@/services/aiService')
      const { provider, apiKey } = getAIConfig()
      const chatLog = newMsgs.map((m) => `${m.userName}: ${m.text}`).join('\n')
      const summary = await callAI(provider, apiKey,
        '당신은 채팅 요약 어시스턴트입니다. 다음 대화를 한국어로 간결하게 토픽별로 요약해주세요. 중요한 결정사항, 할일, 멘션을 강조해주세요.',
        [{ role: 'user', content: `다음 ${newMsgs.length}개의 새 메시지를 요약해주세요:\n\n${chatLog}` }],
        'recap',
        1024
      )
      await onSend({ text: `📌 부재 중 요약 (${newMsgs.length}건)\n\n${summary}`, type: 'system', systemEvent: { action: 'ai_response', actorName: 'AI 어시스턴트' } })
    } catch {
      await onSend({ text: '요약 생성에 실패했습니다.', type: 'system', systemEvent: { action: 'ai_response' } })
    }
    return true
  }

  // /help — 명령어 목록
  if (cmd.startsWith('/help') || cmd.startsWith('/도움')) {
    await onSend({
      text: `📖 사용 가능한 명령어:\n\n/할일 [내용] — 자연어로 할일 생성\n/마감오늘 — 오늘 마감 할일 목록\n/미완료 — 미완료 할일 목록\n/recap — 부재 중 대화 AI 요약\n/help — 이 도움말\n\n💡 @AI [질문] — 프로젝트 AI 어시스턴트에게 질문`,
      type: 'system',
      systemEvent: { action: 'slash_command' },
    })
    return true
  }

  return false // 미인식 명령어는 일반 메시지로 전송
}
